import React, { useState } from "react";
import {
    Text,
    Box,
    Loader,
    Tooltip,
    Collapse,
    ActionIcon,
    Group,
} from "@mantine/core";
import { ConfirmModal } from "@xplatform/design-system";
import {
    IconChevronDown,
    IconChevronUp,
    IconDownload,
    IconCopy,
    IconTrash,
} from "@tabler/icons-react";

type DeleteTarget =
    | { kind: "process"; processId: string; label: string }
    | { kind: "item"; processId: string; itemId: string; label: string };
import {
    ProcessStatus,
    ProcessItemStatus,
    type IProcessInfoViewModel,
    type IProcessItemViewModel,
} from "@models/index";
import { useExtraction } from "@src/hooks/useContext";
import { useTranslation } from "react-i18next";
import { getExtractionErrorMessage } from "@utils/errorUtil";
import EmptyState from "@src/components/EmptyState/EmptyState";
import MappingStatusBadge from "@src/features/MappingStatus/MappingStatusBadge";
import documentIcon from "@assets/icons/document.png";
import failedDocumentIcon from "@assets/icons/failed-document.jpg";
import "./ProcessedResultsList.scss";

const CDN_BASE_URL = import.meta.env.VITE_EXTRACTION_CDN_BASE_URL;

const ProcessedResultsList: React.FC = () => {
    const {
        processResultsInfo,
        selectedProcessItem,
        isLoadingResults,
        getExtractedResult,
        downloadBatchResults,
        deleteProcess,
        deleteProcessItem,
    } = useExtraction();

    const [expandedBatches, setExpandedBatches] = useState<Set<string>>(
        new Set(),
    );
    const [downloadingBatchId, setDownloadingBatchId] = useState<string | null>(
        null,
    );
    const { t } = useTranslation();
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const openDeleteDialog = (e: React.MouseEvent, target: DeleteTarget) => {
        e.stopPropagation();
        setDeleteTarget(target);
    };

    const closeDeleteDialog = () => {
        if (isDeleting) return;
        setDeleteTarget(null);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        const ok =
            deleteTarget.kind === "process"
                ? await deleteProcess(deleteTarget.processId)
                : await deleteProcessItem(
                      deleteTarget.processId,
                      deleteTarget.itemId,
                  );
        setIsDeleting(false);
        if (ok) {
            setDeleteTarget(null);
        }
    };

    const copyProcessId = (e: React.MouseEvent, processId: string) => {
        e.stopPropagation();
        navigator.clipboard.writeText(processId);
        setCopiedId(processId);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const getImageSrc = (status: string, demoImage: string | null) => {
        if (status === ProcessStatus.Failed) {
            return failedDocumentIcon;
        }
        if (
            status === ProcessStatus.Pending ||
            status === ProcessStatus.Processing
        ) {
            return documentIcon;
        }
        if (demoImage && CDN_BASE_URL) {
            return `${CDN_BASE_URL}/${demoImage}`;
        }
        return documentIcon;
    };

    const getDisplayedId = (id: string): string => {
        return id.split("-")[0];
    };

    const isLoading = (status: string) => {
        return (
            status === ProcessStatus.Processing || status === ProcessStatus.Pending
        );
    };

    const isItemProcessing = (status: string) => {
        return (
            status === ProcessItemStatus.Pending ||
            status === ProcessItemStatus.Created ||
            status === ProcessItemStatus.OcrExecution ||
            status === ProcessItemStatus.PageDetection ||
            status === ProcessItemStatus.DataExtraction ||
            status === ProcessItemStatus.ResultMerging
        );
    };

    const getItemStatusLabel = (status: string): string => {
        switch (status) {
            case ProcessItemStatus.Pending:
                return "Pending";
            case ProcessItemStatus.Created:
                return "Created";
            case ProcessItemStatus.OcrExecution:
                return "OCR";
            case ProcessItemStatus.PageDetection:
                return "Detecting";
            case ProcessItemStatus.DataExtraction:
                return "Extracting";
            case ProcessItemStatus.ResultMerging:
                return "Merging";
            case ProcessItemStatus.Completed:
                return "Done";
            case ProcessItemStatus.Skipped:
                return "Skipped";
            case ProcessItemStatus.Failed:
                return "Failed";
            default:
                return status;
        }
    };

    const isFailed = (status: string) => {
        return status === ProcessStatus.Failed;
    };

    const toggleBatchExpand = (batchId: string) => {
        setExpandedBatches((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(batchId)) {
                newSet.delete(batchId);
            } else {
                newSet.add(batchId);
            }
            return newSet;
        });
    };

    const renderSingleItem = (
        processInfo: IProcessInfoViewModel,
        item: IProcessItemViewModel,
        isPartOfBatch: boolean = false,
        index: number = 0,
    ) => {
        const itemStatus = item.status;
        const displayName = item.fileName || getDisplayedId(item.id);
        const isProcessingItem = isItemProcessing(itemStatus);
        const isFailedItem = isFailed(itemStatus);
        const isItemDisabled = isProcessingItem || isFailedItem;
        // Delete is a non-destructive soft delete on the backend, so it is
        // allowed in any status (including pending/processing).
        const isDeletable = true;
        const deleteTargetForItem: DeleteTarget = isPartOfBatch
            ? {
                  kind: "item",
                  processId: processInfo.id,
                  itemId: item.id,
                  label: displayName,
              }
            : {
                  kind: "process",
                  processId: processInfo.id,
                  label: displayName,
              };

        return (
            <Box
                key={`${item.id}-${index}`}
                className={`result-item ${isItemDisabled ? "disabled" : ""} ${isPartOfBatch ? "batch-item" : ""}`}
                onClick={() => !isItemDisabled && getExtractedResult(processInfo, item)}
            >
                <Box
                    className={`image-container ${selectedProcessItem?.id === item.id ? "selected" : ""}`}
                >
                    <img
                        src={getImageSrc(itemStatus, item.demoImage)}
                        alt="Document"
                        onError={(e) => { e.currentTarget.src = documentIcon; }}
                    />
                    {isProcessingItem && (
                        <Box className="status-bar">
                            <Loader size={10} className="status-spinner" />
                            <Text size="xs" className="status-label">
                                {getItemStatusLabel(itemStatus)}
                            </Text>
                        </Box>
                    )}
                    {isFailedItem && (() => {
                        const { message, suggestion } = getExtractionErrorMessage(item.errorMessage, t);
                        return (
                            <Box className="error-message-banner">
                                <Text className="error-message-text">{message}</Text>
                                {suggestion && (
                                    <Text className="error-message-suggestion">{suggestion}</Text>
                                )}
                            </Box>
                        );
                    })()}
                    {isDeletable && (
                        <Tooltip
                            label={t("extraction.processedResults.delete")}
                            position="top"
                        >
                            <ActionIcon
                                size="sm"
                                variant="filled"
                                color="red"
                                radius="xl"
                                className="result-item-delete-button"
                                aria-label={t("extraction.processedResults.delete")}
                                onClick={(e: React.MouseEvent) =>
                                    openDeleteDialog(e, deleteTargetForItem)
                                }
                            >
                                <IconTrash size={12} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Box>
                <Text
                    size="xs"
                    ta="center"
                    fw={700}
                    className={`filename-text ${isFailedItem ? "error" : ""}`}
                >
                    {displayName}
                </Text>
                {isProcessingItem && (
                    <Text size="xs" className="loading-text">
                        Processing...
                    </Text>
                )}
                <MappingStatusBadge
                    processId={processInfo.id}
                    processItemId={item.id}
                    enabled={processInfo.mappingEnabled && itemStatus === ProcessItemStatus.Completed}
                />
            </Box>
        );
    };

    const handleDownloadBatch = (e: React.MouseEvent, processId: string) => {
        e.stopPropagation();
        setDownloadingBatchId(processId);
        downloadBatchResults(processId).finally(() => {
            setDownloadingBatchId(null);
        });
    };

    const renderBatchProcess = (processInfo: IProcessInfoViewModel) => {
        const batchId = getDisplayedId(processInfo.id);
        const isExpanded = expandedBatches.has(processInfo.id);
        const hasError = isFailed(processInfo.status);
        const isProcessing = isLoading(processInfo.status);
        const totalItems = processInfo.items.length;
        const successItemsCount = processInfo.items.filter(
            (item) => item.status === ProcessItemStatus.Completed,
        ).length;
        const canDownload = !isProcessing && !hasError && totalItems > 0;

        return (
            <Box key={processInfo.id} className="batch-container">
                <Box
                    className={`batch-header ${hasError ? "error" : ""}`}
                    onClick={() => toggleBatchExpand(processInfo.id)}
                >
                    <Box className="batch-header-main">
                        <Box className="batch-header-left">
                            <Tooltip
                                label={(() => {
                                    const { message, suggestion } = getExtractionErrorMessage(processInfo.errorMessage, t);
                                    return `${message} ${suggestion}`;
                                })()}
                                position="top"
                                multiline
                                maw={400}
                                style={{ wordBreak: "break-word" }}
                                disabled={!hasError}
                            >
                                <Box className={`batch-badge ${hasError ? "error" : ""}`}>
                                    <Text component="span" className="batch-badge-label">
                                        {batchId}
                                    </Text>
                                    <Tooltip
                                        label={
                                            copiedId === processInfo.id
                                                ? t("extraction.processedResults.copied")
                                                : t("extraction.processedResults.copyBatchId")
                                        }
                                        position="top"
                                    >
                                        <ActionIcon
                                            size="xs"
                                            variant="transparent"
                                            radius="xl"
                                            className="batch-badge-copy-button"
                                            onClick={(e: React.MouseEvent) =>
                                                copyProcessId(e, processInfo.id)
                                            }
                                        >
                                            <IconCopy size={11} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Box>
                            </Tooltip>
                            <Box
                                className={`batch-count-badge ${successItemsCount === totalItems ? "complete" : ""}`}
                            >
                                {`${successItemsCount}/${totalItems}`}
                            </Box>
                        </Box>
                        <Box className="batch-header-right">
                            {canDownload && (
                                <Tooltip label="Download all results as ZIP" position="top">
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        radius="xl"
                                        className="batch-icon-button download-button"
                                        onClick={(e: React.MouseEvent) =>
                                            handleDownloadBatch(e, processInfo.id)
                                        }
                                        disabled={downloadingBatchId === processInfo.id}
                                    >
                                        {downloadingBatchId === processInfo.id ? (
                                            <Loader size={14} />
                                        ) : (
                                            <IconDownload size={14} />
                                        )}
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            <Tooltip
                                label={t("extraction.processedResults.deleteBatch")}
                                position="top"
                            >
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    radius="xl"
                                    color="red"
                                    className="batch-icon-button delete-button"
                                    aria-label={t("extraction.processedResults.deleteBatch")}
                                    onClick={(e: React.MouseEvent) =>
                                        openDeleteDialog(e, {
                                            kind: "process",
                                            processId: processInfo.id,
                                            label: batchId,
                                        })
                                    }
                                >
                                    <IconTrash size={14} />
                                </ActionIcon>
                            </Tooltip>
                            {isProcessing && (
                                <Loader size={16} className="batch-loading-spinner" />
                            )}
                        </Box>
                    </Box>
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        radius="xl"
                        className="batch-icon-button expand-button"
                    >
                        {isExpanded ? (
                            <IconChevronUp size={14} />
                        ) : (
                            <IconChevronDown size={14} />
                        )}
                    </ActionIcon>
                </Box>
                <Collapse in={isExpanded}>
                    <Box className="batch-items-grid">
                        {processInfo.items.map((item, idx) =>
                            renderSingleItem(processInfo, item, true, idx),
                        )}
                    </Box>
                </Collapse>
            </Box>
        );
    };

    const renderProcessInfo = (processInfo: IProcessInfoViewModel) => {
        if (processInfo.isBatch) {
            return renderBatchProcess(processInfo);
        }

        if (isLoading(processInfo.status) && processInfo.items.length === 0) {
            return (
                <Box key={processInfo.id} className="result-item disabled">
                    <Box className="image-container">
                        <img src={documentIcon} alt="Document" />
                        <Box className="loading-overlay">
                            <Loader size={20} className="loading-spinner" />
                        </Box>
                    </Box>
                    <Text size="xs" ta="center" fw={700} className="filename-text">
                        {getDisplayedId(processInfo.id)}
                    </Text>
                    <Text size="xs" className="loading-text">
                        Loading...
                    </Text>
                </Box>
            );
        }

        if (processInfo.items.length > 0) {
            return renderSingleItem(processInfo, processInfo.items[0], false);
        }

        return null;
    };

    if (isLoadingResults) {
        return (
            <Group justify="center" style={{ minHeight: "150px" }}>
                <Loader size={24} />
                <Text size="sm" ml="xs">
                    Loading processed results...
                </Text>
            </Group>
        );
    }

    if (processResultsInfo.length === 0) {
        return (
            <Box ta="center">
                <EmptyState message="No processed results found" variant="body2" />
            </Box>
        );
    }

    return (
        <Box className="processed-results-scrollable-container">
            <Box className="results-grid">
                {processResultsInfo.map((processInfo, index) => (
                    <React.Fragment key={`${processInfo.id}-${index}`}>
                        {renderProcessInfo(processInfo)}
                    </React.Fragment>
                ))}
            </Box>

            <ConfirmModal
                opened={deleteTarget !== null}
                onClose={closeDeleteDialog}
                onConfirm={confirmDelete}
                centered
                size="sm"
                withCloseButton={!isDeleting}
                closeOnClickOutside={!isDeleting}
                closeOnEscape={!isDeleting}
                title={
                    <Text fw={600}>
                        {deleteTarget?.kind === "process"
                            ? t("extraction.processedResults.confirmDeleteProcessTitle")
                            : t("extraction.processedResults.confirmDeleteItemTitle")}
                    </Text>
                }
                overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
                portalProps={{ target: "#extraction-portal-root" }}
                message={
                    deleteTarget?.kind === "process"
                        ? t("extraction.processedResults.confirmDeleteProcessBody", {
                              label: deleteTarget.label,
                          })
                        : deleteTarget
                          ? t("extraction.processedResults.confirmDeleteItemBody", {
                                label: deleteTarget.label,
                            })
                          : ""
                }
                confirmLabel={t("extraction.processedResults.delete")}
                cancelLabel={t("common.cancel")}
                danger
                loading={isDeleting}
            />
        </Box>
    );
};

export default ProcessedResultsList;
